import React, { useCallback, useState } from 'react'

import { HiChevronUpDown } from 'react-icons/hi2'
import { twMerge } from 'tailwind-merge'

import { CompactThumbnail } from '@/components/common/Attachment/Thumbnail'
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxGroup,
  ComboboxInput,
  ComboboxList,
  ComboboxSelectableItem,
  ComboboxTrigger,
} from '@/components/common/ComboboxV2'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/common/Select'
import { TEXT_INPUT_CLASSNAME } from '@/components/common/TextInputV2'
import { getComboboxTriggerClassName } from '@/components/library-v2/ui/Combobox/lib'

import { cleanFileName } from '@/helpers/file'

import type { UnhydratedFile } from '@/types/File'
import type { UUID } from '@/types/Id'

interface FileComboboxProps {
  value: UUID | null
  onChange: (fileId: UUID) => void
  files?: UnhydratedFile[]
  placeholder?: string
  disabled?: boolean
}

export const FileCombobox: React.FC<FileComboboxProps> = ({
  value,
  onChange,
  files,
  placeholder = 'Select file...',
  disabled = false,
}) => {
  const [open, setOpen] = useState<boolean>(false)

  const handleSelect = useCallback(
    (fileId: UUID) => () => {
      onChange(fileId)
      setOpen(false)
    },
    [onChange, setOpen],
  )

  const selectedFile = files?.find((f) => f.id === value)

  return (
    <Combobox open={open} onOpenChange={setOpen}>
      <ComboboxTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={twMerge(
            TEXT_INPUT_CLASSNAME,
            getComboboxTriggerClassName({ disabled }),
          )}
        >
          {selectedFile ? (
            <FileItem file={selectedFile} />
          ) : (
            <span className="text-sm truncate text-primary-500 dark:text-primary-400">
              {placeholder}
            </span>
          )}
          <HiChevronUpDown size={16} className="flex-shrink-0" />
        </button>
      </ComboboxTrigger>
      <ComboboxContent align="start" className="p-0 w-[400px]">
        <ComboboxInput
          placeholder="Search files..."
          className="placeholder:text-sm"
        />
        <ComboboxList>
          <ComboboxEmpty className="text-sm">No files found.</ComboboxEmpty>
          <ComboboxGroup>
            {(files ?? []).map((file) => {
              const name = cleanFileName(file.name)
              return (
                <ComboboxSelectableItem
                  key={file.id}
                  value={`${name}-${file.id}`}
                  isSelected={value === file.id}
                  onSelect={handleSelect(file.id)}
                >
                  <div className="flex items-center gap-2 truncate">
                    <CompactThumbnail name={name} size={16} />
                    <span className="text-sm truncate">{name}</span>
                  </div>
                </ComboboxSelectableItem>
              )
            })}
          </ComboboxGroup>
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  )
}

interface FileItemProps {
  file: UnhydratedFile
}

const FileItem: React.FC<FileItemProps> = ({ file }) => {
  return (
    <div className="flex items-center gap-2 truncate">
      <CompactThumbnail name={file.name} size={16} />
      <span className="text-sm truncate">{cleanFileName(file.name)}</span>
    </div>
  )
}

export const FileSelect: React.FC<FileComboboxProps> = ({
  value,
  onChange,
  files,
  placeholder = 'Select file...',
  disabled = false,
}) => {
  const handleValueChange = useCallback(
    (fileId: string) => {
      onChange(fileId as UUID)
    },
    [onChange],
  )
  const selectedFile = files?.find((f) => f.id === value)

  return (
    <Select
      value={value || ''}
      onValueChange={handleValueChange}
      disabled={disabled}
    >
      <SelectTrigger className="w-full dark:bg-default-dark">
        <SelectValue placeholder={placeholder}>
          {selectedFile ? (
            <FileItem file={selectedFile} />
          ) : (
            <span className="text-sm truncate text-primary-500 dark:text-primary-400">
              {placeholder}
            </span>
          )}
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        {files && files.length > 0 ? (
          files.map((file) => (
            <SelectItem key={file.id} value={file.id}>
              <FileItem file={file} />
            </SelectItem>
          ))
        ) : (
          <div className="text-sm truncate px-2 py-[5px] text-primary-500 dark:text-primary-400">
            <span className="text-sm truncate">No files found</span>
          </div>
        )}
      </SelectContent>
    </Select>
  )
}
